import type { createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler'
import type { SearchFilterSelection, SearchFilterType } from '@/scopes/search/search/infrastructure/api/types'
import type { SearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository'
import type { SearchStore } from '@/scopes/search/search/infrastructure/store/store'
import type { FilterOption } from '@/scopes/search/types'
import type { StoreApi } from 'zustand'
import { scrollToSearchElementDelayed } from '@/scopes/search/search/infrastructure/dom/scrollToSearchElement'
import { createSearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/searchQueryRepository'
import { searchStore as defaultSearchStore } from '@/scopes/search/search/infrastructure/store/store'
import { difference, flatten, isEmpty } from 'ramda'

interface Dependencies {
  searchQueryRepository: SearchQueryRepository
  searchStore: StoreApi<SearchStore>
  filterSelectionQueryParamHandler: ReturnType<typeof createFilterSelectionQueryParamHandler>
}

export function createSelectDestinationActions(dependencies: Partial<Dependencies> = {}) {
  const {
    searchQueryRepository = createSearchQueryRepository(),
    searchStore = defaultSearchStore,
    filterSelectionQueryParamHandler,
  } = dependencies as Dependencies

  function setCountryFilterSelection(newSelectedCountryIds: string[]) {
    const { selectedCountryIds } = getGeographicAreasSelection()

    setGeographicAreaSelection('Country', newSelectedCountryIds)

    const removedCountriesIds = difference(selectedCountryIds, newSelectedCountryIds)
    const removedRegionsIds = removeRelatedRegions(removedCountriesIds)

    const removedSubregionsIds = removeRelatedSubregions(removedRegionsIds)

    const removedRegionsForCities = combineUniqueValues(removedRegionsIds, removedSubregionsIds)

    removeRelatedCities(removedRegionsForCities)
    filterSelectionQueryParamHandler.update()
    scrollToSearchElementDelayed()
  }

  function setRegionFilterSelection(newSelectedRegionIds: string[]) {
    const { selectedRegionIds, selectedCityIds, selectedSubregionIds } = getGeographicAreasSelection()
    const { regionOptions, cityOptions, subRegionOptions } = getGeographicAreasOptions()

    const regionsToAdd = difference(newSelectedRegionIds, selectedRegionIds)
    const regionsToRemove = difference(selectedRegionIds, newSelectedRegionIds)

    const countriesToAdd = getParentGeographicAreasFromSelectedOptions(regionsToAdd, regionOptions)

    const subRegionsToDelete = findRelatedAreaOptions(regionsToRemove, subRegionOptions)
    const newSelectedSubregionsIds = difference(selectedSubregionIds, subRegionsToDelete)
    const citiesToDeleteByRegion = findRelatedAreaOptions(regionsToRemove, cityOptions)
    const citiesToDeleteBySubregion = findRelatedAreaOptions(subRegionsToDelete, cityOptions)
    const citiesToDelete = combineUniqueValues(citiesToDeleteByRegion, citiesToDeleteBySubregion)
    const newSelectedCityIds = difference(selectedCityIds, citiesToDelete)
    setGeographicAreaSelection('Region', newSelectedRegionIds)

    const selectedCountryIds = getParentGeographicAreasFromSelectedOptions(newSelectedRegionIds, regionOptions)
    const newSelectedCountriesIds = combineUniqueValues(selectedCountryIds, countriesToAdd)
    if (newSelectedRegionIds.length > 0) {
      setGeographicAreaSelection('Country', newSelectedCountriesIds)
    }

    if (!isEmpty(subRegionsToDelete)) {
      setGeographicAreaSelection('Subregion', newSelectedSubregionsIds)
    }

    if (!isEmpty(citiesToDelete)) {
      setGeographicAreaSelection('City', newSelectedCityIds)
    }
    filterSelectionQueryParamHandler.update()
    scrollToSearchElementDelayed()
  }

  function setSubregionFilterSelection(newSelectedSubregionIds: string[]) {
    const { selectedRegionIds, selectedSubregionIds, selectedCityIds } = getGeographicAreasSelection()
    const { regionOptions, subRegionOptions, cityOptions } = getGeographicAreasOptions()

    const subRegionsToAdd = difference(newSelectedSubregionIds, selectedSubregionIds)
    const subRegionsToRemove = difference(selectedSubregionIds, newSelectedSubregionIds)

    const regionsToAdd = getParentGeographicAreasFromSelectedOptions(subRegionsToAdd, subRegionOptions)

    const citiesToDelete = findRelatedAreaOptions(subRegionsToRemove, cityOptions)
    const newSelectedCityIds = difference(selectedCityIds, citiesToDelete)

    const newSelectedRegionIds = combineUniqueValues(selectedRegionIds, regionsToAdd)
    if (newSelectedRegionIds.length > 0) {
      setGeographicAreaSelection('Region', newSelectedRegionIds)
    }
    setGeographicAreaSelection('Subregion', newSelectedSubregionIds)
    const selectedCountryIds = getParentGeographicAreasFromSelectedOptions(newSelectedRegionIds, regionOptions)
    if (selectedCountryIds.length > 0) {
      setGeographicAreaSelection('Country', selectedCountryIds)
    }
    if (!isEmpty(citiesToDelete)) {
      setGeographicAreaSelection('City', newSelectedCityIds)
    }
    filterSelectionQueryParamHandler.update()
    scrollToSearchElementDelayed()
  }

  function setCityFilterSelection(newSelectedCityIds: string[]) {
    const { selectedCityIds } = getGeographicAreasSelection()
    const { cityOptions, regionOptions, subRegionOptions } = getGeographicAreasOptions()

    const selectedCityParentIds = getParentGeographicAreasFromSelectedOptions(newSelectedCityIds, cityOptions)
    const selectedRegionIdsFromSubregions = getParentGeographicAreasFromSelectedOptions(selectedCityParentIds, subRegionOptions)
    const selectedRegionIds = getGeographicAreasFromSelectedOptions(combineUniqueValues(selectedRegionIdsFromSubregions, selectedCityParentIds), regionOptions)
    const selectedSubregionIds = getGeographicAreasFromSelectedOptions(combineUniqueValues(selectedRegionIdsFromSubregions, selectedCityParentIds), subRegionOptions)
    const selectedCountryIds = getParentGeographicAreasFromSelectedOptions(selectedCityParentIds, regionOptions)

    const citiesToAdd = difference(newSelectedCityIds, selectedCityIds)
    const parentsToAdd = getParentGeographicAreasFromSelectedOptions(citiesToAdd, cityOptions)
    const subRegionsToAdd = getGeographicAreasFromSelectedOptions(parentsToAdd, subRegionOptions)
    const newSelectedSubregionIds = combineUniqueValues(selectedSubregionIds, subRegionsToAdd)

    const regionsFromSubregionsToAdd = getParentGeographicAreasFromSelectedOptions(subRegionsToAdd, subRegionOptions)
    const regionsToAdd = getGeographicAreasFromSelectedOptions(parentsToAdd, regionOptions)
    const newSelectedRegionIdsFromSubregions = combineUniqueValues(regionsFromSubregionsToAdd, regionsToAdd)
    const newSelectedRegionIds = combineUniqueValues(selectedRegionIds, newSelectedRegionIdsFromSubregions)

    const countriesToAdd = getParentGeographicAreasFromSelectedOptions(newSelectedRegionIds, regionOptions)
    const newSelectedCountriesIds = combineUniqueValues(selectedCountryIds, countriesToAdd)

    if (newSelectedCityIds.length > 0) {
      setGeographicAreaSelection('Country', newSelectedCountriesIds)
      setGeographicAreaSelection('Region', newSelectedRegionIds)
      setGeographicAreaSelection('Subregion', newSelectedSubregionIds)
    }
    setGeographicAreaSelection('City', newSelectedCityIds)
    filterSelectionQueryParamHandler.update()
    scrollToSearchElementDelayed()
  }

  function removeRelatedRegions(countryIds: string[]) {
    const { selectedRegionIds } = getGeographicAreasSelection()
    const { regionOptions } = getGeographicAreasOptions()

    const regionsToRemove = findRelatedAreaOptions(countryIds, regionOptions)
    const regionsToKeep = difference(selectedRegionIds, regionsToRemove)

    if (isEmpty(regionsToRemove)) {
      return []
    }
    setGeographicAreaSelection('Region', regionsToKeep)

    return regionsToRemove
  }

  function removeRelatedSubregions(regionIds: string[]) {
    const { selectedSubregionIds } = getGeographicAreasSelection()
    const { subRegionOptions } = getGeographicAreasOptions()

    const subRegionsToRemove = findRelatedAreaOptions(regionIds, subRegionOptions)
    const subRegionsToKeep = difference(selectedSubregionIds, subRegionsToRemove)

    if (isEmpty(subRegionsToRemove)) {
      return []
    }
    setGeographicAreaSelection('Subregion', subRegionsToKeep)

    return subRegionsToRemove
  }

  function removeRelatedCities(regionIds: string[]) {
    const { selectedCityIds } = getGeographicAreasSelection()
    const { cityOptions } = getGeographicAreasOptions()

    const citiesToRemove = findRelatedAreaOptions(regionIds, cityOptions)
    const citiesToKeep = difference(selectedCityIds, citiesToRemove)

    if (isEmpty(citiesToRemove)) {
      return []
    }
    setGeographicAreaSelection('City', citiesToKeep)

    return citiesToRemove
  }

  function getGeographicAreasOptions() {
    const activeQuery = searchQueryRepository.getActiveQuery()

    return {
      regionOptions: activeQuery?.filters?.Region?.options ?? [],
      subRegionOptions: activeQuery?.filters?.Subregion?.options ?? [],
      cityOptions: activeQuery?.filters?.City?.options ?? [],
    }
  }

  function getGeographicAreasSelection() {
    const { filterSelection } = searchStore.getState()

    return {
      selectedCountryIds: (filterSelection?.Country ?? []) as string[],
      selectedRegionIds: (filterSelection?.Region ?? []) as string[],
      selectedSubregionIds: (filterSelection?.Subregion ?? []) as string[],
      selectedCityIds: (filterSelection?.City ?? []) as string[],
    }
  }

  function setGeographicAreaSelection<K extends SearchFilterType>(areaFilterType: K, value: SearchFilterSelection[K]) {
    const { setFilterSelection } = searchStore.getState()
    setFilterSelection(areaFilterType, value)
  }

  function getParentGeographicAreasFromSelectedOptions(areaIds: string[], options: FilterOption<string, { ParentId: string }>[]) {
    return Array.from(
      new Set(options.filter(({ value }) => areaIds.includes(value)).map(({ metadata }) => metadata.ParentId)),
    )
  }

  function getGeographicAreasFromSelectedOptions(areaIds: string[], areaOptions: FilterOption<string, { ParentId: string }>[]) {
    return areaOptions.filter(({ value }) => areaIds.includes(value)).map(({ value }) => value)
  }

  function findRelatedAreaOptions(areaIds: string[], areaOptions: FilterOption<string, { ParentId: string }>[]) {
    return areaOptions.filter(({ metadata }) => areaIds.includes(metadata.ParentId)).map(({ value }) => value)
  }

  function combineUniqueValues(...values: string[][]) {
    return Array.from(new Set(flatten(values)))
  }

  return { setCountryFilterSelection, setRegionFilterSelection, setSubregionFilterSelection, setCityFilterSelection }
}
